// ** React Imports
import { useState, useEffect, ChangeEvent } from 'react'

// ** Type Imports
import { GoogleAdsTagsType, GoogleAdsType, WebhookType, WebhooksType } from 'src/lib/types/integrations'

// ** Child Imports
import Alerts from 'src/components/common/feedback-and-dialog/Alerts'
import { Checkbox, Switch, Grid, Typography, TextField } from '@mui/material'
import ConfirmationDialog from 'src/components/common/feedback-and-dialog/ConfirmationDialog'
import InputTextField from 'src/components/common/form-elements/InputTextField'
import SaveNotification from 'src/components/common/SaveNotification'
import LayoutContainer from 'src/components/common/containers/LayoutContainer'
import { Check, Edit, PlusSquare, Trash2 } from 'react-feather'
import IntegrationChannels from 'src/components/settings/IntegrationChannels'
import { LoadingButton } from '@mui/lab'

// ** Context Imports
import { useIntegrations } from 'src/lib/contexts/IntegrationsContext'
import { useLoader } from 'src/lib/contexts/LoaderContext'

// ** Util Imports
import { getIntegrations, setAnalyticsConfig } from 'src/lib/core/api-methods'
import { defaultWebhookEventName, initialWebhooksState } from 'src/lib/utils/integrationsHelpers'
import { alertSeverites } from 'src/lib/utils/constants'
import { useTemporary } from 'src/lib/contexts/TemporaryContext'
import useConfirmOnRouteChanges from 'src/lib/utils/custom-hooks/useConfirmOnRouteChanges'
import { useLocale } from 'src/lib/hooks/useLocal'
import { postRequest } from 'src/lib/core/api-client'
import { apiURI } from 'src/lib/utils/constants'
import { parseWebhhoksListPayload } from 'src/lib/utils/parsers'
import { getDefaultChannels } from 'src/lib/utils/helpers'

interface WebhooktrackingFormProps {
  webhook: WebhookType
  index: number
  webhooksAnalytics: WebhooksType
  setWebhooksAnalytics: React.Dispatch<React.SetStateAction<WebhooksType>>
}

const WebhooktrackingForm: React.FC<WebhooktrackingFormProps> = ({
  webhook,
  index,
  webhooksAnalytics,
  setWebhooksAnalytics
}) => {
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState<boolean>(false)
  const [isVerfiying, setIsVerifying] = useState<boolean>(false)
  const removeWebhook = async () => {
    const modifiedWebhooks = [...webhooksAnalytics?.webhooks]
    modifiedWebhooks.splice(index, 1)
    setWebhooksAnalytics({
      ...webhooksAnalytics,
      webhooks: modifiedWebhooks
    })
  }

  const onAddWebhookHeaderBtnClick = () => {
    if (Object.keys(webhook).includes('')) {
      alert('Please save the unsaved headers first')
      return
    }
    const newWebhook = { ...webhook }
    newWebhook.headers[''] = ''
    const modifiedWebhooks = [...webhooksAnalytics?.webhooks]
    modifiedWebhooks[index] = newWebhook
    setWebhooksAnalytics({
      ...webhooksAnalytics,
      webhooks: modifiedWebhooks
    })
  }

  const verifyWebhookConfig = async () => {
    const webhookPayload = {
      enabled: webhooksAnalytics?.enabled,
      webhooks: parseWebhhoksListPayload(webhooksAnalytics?.webhooks)
    }

    try {
      setIsVerifying(true)
      const testResponse: any = await postRequest(
        '/dashboard/analytics/webhooks/config/verify',
        apiURI.CHECKOUT_NODE,
        webhookPayload
      )
      if (testResponse?.error) {
        alert(testResponse?.error)
        setIsVerifying(false)
        return
      }
      alert(testResponse?.data?.message)
      setIsVerifying(false)
    } catch (e) {
      alert('Something went wrong.')
      setIsVerifying(false)
    }
  }

  return (
    <>
      <IntegrationChannels
        channels={webhook.channels ?? []}
        onChange={(channels: any) => {
          const newWebhook: WebhookType = { ...webhook }
          newWebhook.channels = channels
          const modifiedWebhooks = [...webhooksAnalytics?.webhooks]
          modifiedWebhooks[index] = newWebhook
          setWebhooksAnalytics({ ...webhooksAnalytics, webhooks: modifiedWebhooks })
        }}
        hideChannels={!webhook?.enabled}
      >
        <Grid display={'flex'} flexDirection={'column'} sx={{ gap: 2, width: '100%' }}>
          <div className='flex justify-start gap-2'>
            <div className='flex items-center justify-between w-full'>
              <Typography variant='body1' className='min-w-fit' sx={{ fontWeight: 600 }}>
                Webhook Configuration
              </Typography>
              <button
                className='flex items-center text-sm font-semibold bg-white text-coal-dark w-fit'
                onClick={() => setIsDeleteConfirmOpen(!isDeleteConfirmOpen)}
              >
                <Trash2 size='16' />
              </button>
            </div>
          </div>
          <Grid item xs={12} md={12} margin={'1rem 0rem'} display={'flex'} flexDirection={'column'} gap={2}>
            <Grid display={'flex'} alignItems={'center'} justifyContent={'center'} gap={4}>
              <Grid xs={3} md={3} justifyContent={'center'}>
                <Typography variant='body2' sx={{ whiteSpace: 'nowrap' }}>
                  URL:
                </Typography>
              </Grid>
              <Grid xs={9} md={9}>
                <TextField
                  fullWidth
                  type='text'
                  size='small'
                  variant='outlined'
                  value={webhook?.url}
                  placeholder='https://'
                  inputProps={{ 'aria-label': 'controlled' }}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    const value = event?.target?.value
                    const newWebhook = { ...webhook }
                    newWebhook.url = value
                    const modifiedWebhooks = [...webhooksAnalytics?.webhooks]
                    modifiedWebhooks[index] = newWebhook
                    setWebhooksAnalytics({
                      ...webhooksAnalytics,
                      webhooks: modifiedWebhooks
                    })
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          {webhook?.authorization && webhook?.authorization !== '' && (
            <Grid item xs={12} md={12} margin={'1rem 0rem'} display={'flex'} flexDirection={'column'} gap={2}>
              <Grid display={'flex'} alignItems={'center'} justifyContent={'center'} gap={4}>
                <Grid xs={3} md={3} justifyContent={'center'}>
                  <Typography variant='body2' sx={{ whiteSpace: 'nowrap' }}>
                    Authorization:
                  </Typography>
                </Grid>
                <Grid xs={9} md={9}>
                  <TextField
                    fullWidth
                    type='text'
                    size='small'
                    variant='outlined'
                    value={webhook?.authorization}
                    placeholder='xxxxxxx'
                    inputProps={{ 'aria-label': 'controlled' }}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      const value = event?.target?.value
                      const newWebhook = { ...webhook }
                      newWebhook.authorization = value
                      const modifiedWebhooks = [...webhooksAnalytics?.webhooks]
                      modifiedWebhooks[index] = newWebhook
                      setWebhooksAnalytics({
                        ...webhooksAnalytics,
                        webhooks: modifiedWebhooks
                      })
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
          {Object.keys(webhook?.headers)?.map((key, ind) => (
            <WebhookHeader
              key={ind}
              name={key}
              value={webhook.headers[key]}
              webhooksAnalytics={webhooksAnalytics}
              setWebhooksAnalytics={setWebhooksAnalytics}
              index={index}
            />
          ))}

          <div className='flex flex-wrap justify-end gap-4'>
            <button
              className='flex items-center gap-1 px-3 py-2 text-sm font-semibold bg-white border rounded-lg border-flo-gray-light text-coal-dark w-fit'
              onClick={onAddWebhookHeaderBtnClick}
            >
              <PlusSquare size='16' />
              Add Header
            </button>
          </div>

          <Grid item xs={12} display={'flex'} alignItems={'center'}>
            <Grid container>
              <Grid item xs={10} md={11} display={'flex'} alignItems={'center'}>
                <Typography variant='body2'>Webhook Configuration is </Typography>
                <Typography variant='body1' sx={{ fontWeight: 500, marginLeft: 2 }}>
                  {`${webhook?.enabled ? 'enabled' : 'disabled'}`}
                </Typography>
              </Grid>

              <Grid item xs={2} md={1} display={'flex'} alignItems={'center'} justifyContent={'end'}>
                <Switch
                  name='webEngageToggle'
                  checked={webhook?.enabled}
                  inputProps={{ 'aria-label': 'controlled' }}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    const value = event?.target?.checked
                    const newWebhook = { ...webhook }
                    newWebhook.enabled = value
                    const modifiedWebhooks = [...webhooksAnalytics?.webhooks]
                    modifiedWebhooks[index] = newWebhook
                    setWebhooksAnalytics({
                      ...webhooksAnalytics,
                      webhooks: modifiedWebhooks
                    })
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <LoadingButton
            loading={isVerfiying}
            variant='outlined'
            size='medium'
            sx={{ width: '240px' }}
            disabled={!Boolean(webhook?.url) || !Boolean(webhook?.enabled || !Boolean(webhooksAnalytics?.enabled))}
            onClick={() => verifyWebhookConfig()}
          >
            Verify webhook configuration
          </LoadingButton>
        </Grid>
      </IntegrationChannels>
      {isDeleteConfirmOpen ? (
        <ConfirmationDialog
          jobButton={'Yes, remove configuration'}
          cancelButton={'No'}
          jobButtonColor='error'
          cancelButtonColor='secondary'
          confirmationText={'Are you sure you want to remove the webhook configuration?'}
          title={'Remove webhook configuration'}
          isOpen={isDeleteConfirmOpen}
          setIsConfirmationOpen={setIsDeleteConfirmOpen}
          handleJob={removeWebhook}
        />
      ) : (
        <></>
      )}
    </>
  )
}

interface WebhookHeaderProps {
  name: string
  value: string
  index: number
  webhooksAnalytics: any
  setWebhooksAnalytics: any
}

const WebhookHeader: React.FC<WebhookHeaderProps> = ({
  name,
  value,
  index,
  webhooksAnalytics,
  setWebhooksAnalytics
}) => {
  const [isEditable, setIsEditable] = useState(name === '' && value === '' ? true : false)
  const [headerName, setHeaderName] = useState(name)
  const [headerValue, setHeaderValue] = useState(value)

  useEffect(() => {
    setHeaderName(name)
    setHeaderValue(value)
  }, [name, value])

  const handleEdit = () => {
    if (isEditable) {
      if (headerName === '' || headerValue === '') {
        alert('Please Enter Header Name and Value')
        return
      }
      if (Object.keys(webhooksAnalytics?.webhooks[index]?.headers)?.includes(headerName) && headerName !== name) {
        alert('Header Name already exists')
        return
      }
      const newHeaders = { ...webhooksAnalytics?.webhooks[index]?.headers }
      newHeaders[headerName] = headerValue
      if (headerName !== name) delete newHeaders[name]
      const newWebhook = { ...webhooksAnalytics?.webhooks[index], headers: newHeaders }
      const modifiedWebhooks = [...webhooksAnalytics?.webhooks]
      modifiedWebhooks[index] = newWebhook
      setWebhooksAnalytics({
        ...webhooksAnalytics,
        webhooks: modifiedWebhooks
      })
    }
    setIsEditable(!isEditable)
  }

  const handleDelete = () => {
    const newHeaders = { ...webhooksAnalytics?.webhooks[index]?.headers }
    if (Object.keys(webhooksAnalytics?.webhooks[index]?.headers)?.includes(headerName)) {
      if (headerName !== name) delete newHeaders[name]
      else delete newHeaders[headerName]
    } else {
      delete newHeaders['']
    }
    const newWebhook = { ...webhooksAnalytics?.webhooks[index], headers: newHeaders }
    const modifiedWebhooks = [...webhooksAnalytics?.webhooks]
    modifiedWebhooks[index] = newWebhook
    setWebhooksAnalytics({
      ...webhooksAnalytics,
      webhooks: modifiedWebhooks
    })
  }

  return (
    <Grid item xs={12} md={12} margin={'1rem 0rem'} display={'flex'} flexDirection={'column'} gap={2}>
      <Grid display={'flex'} alignItems={'end'} justifyContent={'space-between'} gap={4}>
        <InputTextField
          textFieldId='Header-Name'
          label='Header Name'
          inputPlaceholder='Header Name'
          isDisabled={!isEditable}
          isFullWidth
          inputValue={headerName}
          inputProps={{ 'aria-label': 'controlled' }}
          handleChange={(event: ChangeEvent<HTMLInputElement>) => {
            setHeaderName(event?.target?.value)
          }}
        />
        <InputTextField
          textFieldId='Header-Value'
          label='Header Value'
          inputPlaceholder='Header Value'
          isDisabled={!isEditable}
          isFullWidth
          inputValue={headerValue}
          inputProps={{ 'aria-label': 'controlled' }}
          handleChange={(event: ChangeEvent<HTMLInputElement>) => {
            setHeaderValue(event?.target?.value)
          }}
        />
        <button className='border-solid border-2 rounded-md p-1.5' onClick={handleEdit}>
          {isEditable ? <Check /> : <Edit />}
        </button>
        <button className='border-solid border-2 rounded-md p-1.5' onClick={handleDelete}>
          <Trash2 />
        </button>
      </Grid>
    </Grid>
  )
}

interface ACRWebhooksProps {
  isOnboarding?: boolean
  handleOnboardingSave?: () => void
}

const ACRWebhooks: React.FC<ACRWebhooksProps> = ({ isOnboarding = false, handleOnboardingSave }) => {
  // Context hook
  const { setLoaderProperties } = useLoader()
  const { integrations, setIntegrations } = useIntegrations()
  const { temporary, setTemporaryData } = useTemporary()
  const { t } = useLocale()
  const { webhooks } = integrations || {}

  //Local State
  const [webhooksAnalytics, setWebhooksAnalytics] = useState<WebhooksType>(initialWebhooksState)
  const [openAlert, setOpenAlert] = useState<string>('')
  const [alertMessage, setAlertMessage] = useState<string>('')
  const [settingsUpdated, setSettingsUpdated] = useState<boolean>(false)

  useEffect(() => {
    callGetAnalytics()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!temporary) return
    setWebhooksAnalytics(temporary.integrations?.webhooks)
    setTemporaryData(undefined)
  }, [temporary, setTemporaryData])

  useEffect(() => {
    setSettingsUpdated(Boolean(JSON.stringify(webhooks) !== JSON.stringify(webhooksAnalytics)))
  }, [webhooksAnalytics, webhooks])

  useEffect(() => {
    if (!webhooks) return
    setWebhooksAnalytics(webhooks)
    setTemporaryData(undefined)
    setSettingsUpdated(false)
  }, [webhooks, setTemporaryData])

  useConfirmOnRouteChanges(settingsUpdated, () => {
    return new Promise((resolve: any, reject) => {
      if (confirm('You have unsaved changes. Do you want to stay on the page?')) {
        setTemporaryData({
          integrations: {
            ...integrations,
            webhooks: webhooksAnalytics
          }
        })
        resolve(true)
      } else reject(false)
    })
  })

  const callGetAnalytics = async () => {
    try {
      const response: any = await getIntegrations()
      if (response?.error) {
        handleAlerts(alertSeverites.SEVERITY_ERROR, response?.error)
      } else {
        setIntegrations(response?.data)
      }
    } catch (error) {
      handleAlerts(alertSeverites.SEVERITY_ERROR, 'Could not fetch analytics configuration!')
    }
  }

  const isValidForm = () => {
    if (webhooksAnalytics?.enabled) {
      let message = ''
      if (!webhooksAnalytics?.webhooks.length) {
        message = 'Please add webhook configurations to enable tracking'
      } else {
        webhooksAnalytics?.webhooks?.forEach((webhook: any) => {
          if (
            !webhook?.url.match(
              /^(http(s):\/\/.)[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)$/g
            )
          ) {
            message = 'Invalid URL'
          }
          if (!Boolean(webhook.url?.length)) {
            message = 'Please enter correct webhook URL!'
          }
        })
      }
      if (message) {
        setOpenAlert('error')
        setAlertMessage(message)
        return false
      }
    }
    return true
  }

  const callSubmit = async () => {
    if (!isValidForm()) return
    try {
      setLoaderProperties({ isLoaderVisible: true })
      const payload = {
        enabled: webhooksAnalytics?.enabled,
        webhooks: parseWebhhoksListPayload(webhooksAnalytics?.webhooks)
      }
      const response: any = await setAnalyticsConfig('webhooks', payload)
      if (response?.error) {
        handleAlerts(alertSeverites.SEVERITY_ERROR, response?.error)
      } else {
        setIntegrations({ ...integrations, webhooks: webhooksAnalytics }, false)
        setOpenAlert('success')
        setAlertMessage('Successfully saved analytics configuration!')
        handleOnboardingSave && handleOnboardingSave()
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errorMessage
        ? error?.response?.data?.errorMessage
        : `Could not save configuration!`
      handleAlerts(alertSeverites.SEVERITY_ERROR, errorMessage)
      console.error(error)
    }
    setLoaderProperties({ isLoaderVisible: false })
  }

  const handleDiscardSaving = () => {
    if (!webhooks) return
    setWebhooksAnalytics(webhooks)
    setSettingsUpdated(false)
    setTemporaryData(undefined)
  }

  const onAddWebhookBtnClick = async () => {
    const defaultChannels = await getDefaultChannels()
    const modifiedWebhooks = [...webhooksAnalytics?.webhooks]
    modifiedWebhooks.push({
      eventName: defaultWebhookEventName,
      enabled: false,
      url: '',
      headers: [],
      channels: defaultChannels
    })
    setWebhooksAnalytics({
      ...webhooksAnalytics,
      webhooks: modifiedWebhooks
    })
  }

  const handleAlerts = (severity: string, message?: string) => {
    setOpenAlert(severity)
    message && setAlertMessage(message)
  }

  const handleCloseAlert = (event: any, reason: any) => {
    if (reason === 'clickaway') {
      return
    }
    handleAlerts('')
  }

  return (
    <>
      <LayoutContainer>
        <Grid item xs={12} md={12} display={'flex'} alignItems={'center'}>
          <Grid container>
            <Grid item xs={10} md={11} display={'flex'} alignItems={'center'}>
              <Typography variant='body2'>Webhooks Tracking is</Typography>
              <Typography variant='body1' sx={{ fontWeight: 500, marginLeft: 2 }}>
                {`${webhooksAnalytics?.enabled ? 'enabled' : 'disabled'}`}
              </Typography>
            </Grid>
            <Grid item xs={2} md={1} display={'flex'} alignItems={'center'} justifyContent={'end'}>
              <Switch
                name='webhooksToggle'
                checked={webhooksAnalytics?.enabled}
                inputProps={{ 'aria-label': 'controlled' }}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  const checked = event?.target?.checked
                  setWebhooksAnalytics({ ...webhooksAnalytics, enabled: checked })
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </LayoutContainer>
      {webhooksAnalytics.webhooks?.map((tag: any, index: number) => (
        <WebhooktrackingForm
          key={index}
          webhook={tag}
          index={index}
          setWebhooksAnalytics={setWebhooksAnalytics}
          webhooksAnalytics={webhooksAnalytics}
        />
      ))}
      {webhooksAnalytics?.webhooks?.length < 1 && (
        <div className='flex flex-wrap gap-4'>
          <button
            className='flex items-center gap-1 px-3 py-2 text-sm font-semibold bg-white border rounded-lg border-flo-gray-light text-coal-dark w-fit'
            onClick={onAddWebhookBtnClick}
          >
            <PlusSquare size='16' />
            Add Webhook Configuration
          </button>
        </div>
      )}

      {!Boolean(isOnboarding) ? (
        <SaveNotification onSave={callSubmit} onDiscard={handleDiscardSaving} isVisible={settingsUpdated} />
      ) : (
        <button
          className='px-3 py-2 ml-auto text-sm font-bold border rounded-lg w-fit text-[#FFFFFF] bg-[#01CCA7]'
          onClick={callSubmit}
        >
          {t('save')}
        </button>
      )}
      {openAlert.length ? (
        <Alerts
          open={true}
          duration={2000}
          closeHandler={handleCloseAlert}
          severity={openAlert}
          message={alertMessage}
        />
      ) : null}
    </>
  )
}
export default ACRWebhooks
